import { computed, DestroyRef, inject, Signal, signal } from '@angular/core';
import { UmmeldungStore } from '../ummeldung.store';
import { injectWizardContext, isOfEventType, WizardContext, WizardEventType } from '@mwe/features/wizard';
import { apply, applyWhen, disabled, FieldTree, form, SchemaOrSchemaFn, SchemaPath, SchemaPathTree } from '@angular/forms/signals';
import { markAllAsTouched } from '@shared-ikp/utils/signal-forms.util';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { UmmeldungStoreType, UnterschreibendePartei } from '../models';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const DEFAULT_SYNC_EVENTS = [WizardEventType.BeforeNextStepNavigation, WizardEventType.BeforePrevStepNavigation];

/**
 * How strictly a step is validated. `draft` is what navigation and "Speichern und später fortsetzen"
 * need: whatever is entered must be well-formed, but may be incomplete. `complete` additionally
 * enforces the step's `completionSchema` — every required field — and is what signing needs.
 */
export type WizardStepValidationMode = 'draft' | 'complete';

/** What process-level code needs from a step's draft, independent of the draft's shape. */
export interface WizardStepDraft {
  /**
   * Marks every field touched so its errors show, then reports whether the draft is valid in the
   * given mode. The mode sticks: after a failed `complete` validation the required-field errors
   * stay visible until the next `draft` validation (i.e. the next navigation or save attempt).
   */
  touchAndValidate(mode?: WizardStepValidationMode): boolean;
  /** Writes the draft back onto the store's Ummeldung. */
  commitDraft(): void;
  /**
   * Opens the step for a correction: if the signature given in this step (`signature.signedBy`) is among the
   * revoked ones, it is removed from the draft. Steps without `signature` are left as they are. Commits nothing.
   */
  openForEditInputs(revokedUnterschriften: UnterschreibendePartei[]): void;
}

export interface WizardStepForm<TFormModel> extends WizardStepDraft {
  /** The Signal Forms tree the template binds to. */
  readonly field: FieldTree<TFormModel>;
  /** True while `frozenWhen` holds — the whole draft is disabled and shown read-only. */
  readonly frozen: Signal<boolean>;
}

/**
 * The draft of each instantiated step, keyed by the step's own `WizardContext`. Filled by
 * {@link wizardStepForm}, read by {@link stepFormOf}. Steps that were never shown (e.g. the ones
 * before the resume step) have no entry — the wizard never validates them either, and everything
 * that spans steps is derived from the `UmmeldungStore` instead.
 */
const stepForms = new WeakMap<WizardContext, WizardStepDraft>();

export interface WizardStepFormOptions<TFormModel> {
  /** Maps the persisted Ummeldung (store truth) into this step's editable form model. */
  fromStore: (ummeldung: Ummeldung | null, store: UmmeldungStoreType) => TFormModel;
  /** Maps the edited form model back into a partial Ummeldung to patch onto the store. */
  toStore: (draft: TFormModel, store: UmmeldungStoreType) => Partial<Ummeldung>;
  /** Signal Forms schema with this step's validation rules. */
  schema?: SchemaOrSchemaFn<TFormModel>;
  /**
   * Rules a draft may violate but a complete step may not — the required fields. Only applied while
   * validating in `complete` mode, see {@link WizardStepValidationMode}.
   */
  completionSchema?: SchemaOrSchemaFn<TFormModel>;
  /** Wizard events on which the draft is written back to the store. Defaults to next/prev navigation. */
  syncOn?: WizardEventType[];
  /**
   * While this returns true the step is frozen: the whole draft is disabled, so every `[formField]`
   * control renders read-only and validation is skipped. Re-evaluated reactively — a signed step
   * reads its own draft, the steps before it read the store.
   */
  frozenWhen?: (draft: TFormModel, store: UmmeldungStoreType) => boolean;
  /**
   * Only for steps in which a signature is given (e.g. 3 and 4): `signedBy` is who signs in this step,
   * `withoutSignature` returns the draft without that signature. Needed because a fresh signature only lives
   * in the draft until the user navigates, so revoking it in the store alone would not unfreeze the step.
   */
  signature?: { signedBy: UnterschreibendePartei; withoutSignature: (draft: TFormModel) => TFormModel };
}

/**
 * The one way an ummeldung-wizard step edits the Ummeldung.
 *
 * The `UmmeldungStore` remains the single source of persisted truth. `ummeldungWizardStartResolver`
 * has filled it before any step exists, so the draft is built once, synchronously. Each step owns a
 * short-lived draft in the shape its UI needs — which is deliberately *not* the DTO shape — realized
 * as a Signal Forms tree:
 *
 *   store --fromStore()--> draft signal --form(schema)--> template
 *   store <--toStore()--- draft
 *
 * The draft is written back on navigation and on "Speichern und später fortsetzen" only. Anything
 * the user entered in between — a signature included — lives in the draft alone and is gone after
 * a reload, which is what makes a signature revocable until the user moves on.
 */
export function wizardStepForm<TFormModel>(options: WizardStepFormOptions<TFormModel>): WizardStepForm<TFormModel> {
  const store = inject(UmmeldungStore);
  const context = inject(WizardContext);
  const destroyRef = inject(DestroyRef);

  const draft = signal(options.fromStore(store.activeUmmeldung(), store));
  const validationMode = signal<WizardStepValidationMode>('draft');
  const field = form(draft, (path: SchemaPathTree<TFormModel>) => {
    const rootPath = path as SchemaPath<TFormModel>;
    if (options.schema !== undefined) {
      apply(rootPath, options.schema);
    }
    if (options.completionSchema !== undefined) {
      applyWhen(rootPath, () => validationMode() === 'complete', options.completionSchema);
    }
    if (options.frozenWhen !== undefined) {
      disabled(rootPath, ({ value }) => options.frozenWhen(value(), store));
    }
  });

  const stepForm: WizardStepForm<TFormModel> = {
    field,
    frozen: computed(() => field().disabled()),
    touchAndValidate: (mode = 'draft') => {
      validationMode.set(mode);
      markAllAsTouched(field as FieldTree<unknown>);
      return !field().invalid();
    },
    commitDraft: () => store.updateActiveUmmeldung(options.toStore(draft(), store)),
    openForEditInputs: revokedUnterschriften => {
      if (options.signature && revokedUnterschriften.includes(options.signature.signedBy)) {
        draft.update(options.signature.withoutSignature);
      }
    },
  };

  stepForms.set(context, stepForm);
  destroyRef.onDestroy(() => {
    if (stepForms.get(context) === stepForm) {
      stepForms.delete(context);
    }
  });

  context.controller.events$
    .pipe(
      filter(event => context.controller.currentStep()?.context === context && isOfEventType(event, options.syncOn ?? DEFAULT_SYNC_EVENTS)),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(() => stepForm.commitDraft());

  return stepForm;
}

/**
 * The draft of the given step, or of the step on screen when called without arguments in an
 * injection context. `undefined` for steps without a `wizardStepForm`.
 */
export function stepFormOf(context: WizardContext | undefined = injectWizardContext()): WizardStepDraft | undefined {
  return context ? stepForms.get(context) : undefined;
}
